import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import usePermissions from "../../../../Hooks/usePermissions";
import {
    MapContainer,
    TileLayer,
    GeoJSON,
    LayersControl,
    Marker,
    Popup,
    Tooltip,
    useMap,
    Polyline,
    Polygon,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { DOMParser } from "@xmldom/xmldom";
import { kml } from "@tmcw/togeojson";
import L from "leaflet";
import "./DisturbanceTrack.css";
import { FaCircleInfo } from "react-icons/fa6";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

// Import Material React Table and its dependencies
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Select, MenuItem, FormControl, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { Modal } from 'react-bootstrap';
import domtoimage from 'dom-to-image-more';
import { generateCycloneTrackingReport } from './reportGenerator_DT.js';
import { MapHomeControl } from "../../../common/MapControls/MapControls";
// Satellite Imagery now lives on the Dashboard's Forecast map tab — import kept, commented out, for reference.
// import SatelliteImagery from "../SatelliteImagery/SatelliteImagery";

const INITIAL_VIEW_STATE = {
    longitude: 125.7275,
    latitude: -8.8742,
    zoom: 5,
};

// List of available cyclone files
const CYCLONE_FILES = [];

// Function to fetch active cyclones from API
const fetchActiveCyclones = async () => {
    try {
        // Try using the proxy endpoint first
        // const response = await axios.get('/api/api_mobile/api_cyclone/active_cyclones_get');
        const response = await axios.get('https://gcf-app.rimes.int/api_mobile/api_cyclone/active_cyclones_get');
        console.log('Active Cyclones (via proxy):', response.data);
        return response.data;
    } catch (proxyError) {
        console.warn('Proxy request failed, trying direct API call:', proxyError.message);
        try {
            // Fallback to direct API call
            const response = await axios.get('https://gcf-app.rimes.int/api_mobile/api_cyclone/active_cyclones_get');
            console.log('Active Cyclones (direct):', response.data);
            return response.data;
        } catch (directError) {
            console.error('Both proxy and direct API calls failed');
            console.error('Direct error:', directError.message);
            if (directError.response) {
                console.error('Response status:', directError.response.status);
                console.error('Response data:', directError.response.data);
            }
            return [];
        }
    }
};

// Helper function to convert tsr_product codes to readable names
const mapTsrProduct = (productCode) => {
    const productMap = {
        'GH': 'Gust History',
        'GF': 'Gust Forecast',
        'RA': 'GFS Rainfall',
        'WP': 'Wind Probabilities'
    };
    return productMap[productCode] || productCode;
};

// Helper function to convert comma-separated product codes to readable names
const convertProductCodes = (productString) => {
    if (!productString) return '';
    return productString
        .split(',')
        .map((code) => mapTsrProduct(code.trim()))
        .join(', ');
};

// Helper: convert "18:00 UT, 23 Feb 2026" → "2026012318"
// Months are 0-indexed: Jan=00, Feb=01, …, Dec=11
const convertGfsDate = (gfsDateStr) => {
    if (!gfsDateStr) return '';
    const monthMap = {
        'Jan': '00', 'Feb': '01', 'Mar': '02', 'Apr': '03',
        'May': '04', 'Jun': '05', 'Jul': '06', 'Aug': '07',
        'Sep': '08', 'Oct': '09', 'Nov': '10', 'Dec': '11'
    };
    const match = gfsDateStr.match(/(\d{2}):\d{2}\s+UT,\s+(\d{1,2})\s+(\w{3})\s+(\d{4})/);
    if (!match) return '';
    const [, hour, day, monthStr, year] = match;
    const month = monthMap[monthStr] || '00';
    const dayPadded = day.padStart(2, '0');
    return `${year}${month}${dayPadded}${hour}`;
};

const InfoButton = ({ onClick }) => {
    // Leaflet's own drag/click handling swallows clicks on overlay controls
    // unless propagation to the map is explicitly disabled on the DOM node.
    const btnRef = useCallback((node) => {
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    return (
        <div
            ref={btnRef}
            style={{
                position: "absolute",
                bottom: "20px",
                left: "12px",
                padding: "8px",
                borderRadius: "8px",
                fontSize: "15px",
                cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
                zIndex: 1000,
            }}
            onClick={onClick}
        >
            <FaCircleInfo />
        </div>
    );
};

const MapViewAdjuster = ({ features }) => {
    const map = useMap();

    useEffect(() => {
        if (features.length > 0) {
            // Collect all lat/lng values to compute bounds manually.
            // We avoid L.latLngBounds here because Leaflet wraps longitudes
            // beyond ±180 back into [-180,180], which breaks antimeridian-fixed
            // coordinates (e.g. 184.5° would become -175.5°, splitting the view).
            let minLat = Infinity, maxLat = -Infinity;
            let minLng = Infinity, maxLng = -Infinity;

            const processCoord = (coord) => {
                // coord is [lng, lat] in GeoJSON
                const lat = coord[1];
                const lng = coord[0];
                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
                if (lng < minLng) minLng = lng;
                if (lng > maxLng) maxLng = lng;
            };

            features.forEach((feature) => {
                if (!feature || !feature.geometry) return;
                if (feature.geometry.type === "Point") {
                    processCoord(feature.geometry.coordinates);
                } else if (feature.geometry.type === "Polygon") {
                    feature.geometry.coordinates[0].forEach(processCoord);
                } else if (feature.geometry.type === "LineString") {
                    feature.geometry.coordinates.forEach(processCoord);
                } else if (feature.geometry.type === "MultiPolygon") {
                    feature.geometry.coordinates.forEach((poly) =>
                        poly[0].forEach(processCoord)
                    );
                }
            });

            if (minLat !== Infinity) {
                // Build bounds from raw arrays — Leaflet accepts [[lat,lng],[lat,lng]]
                // and won't wrap when passed as a plain array to fitBounds
                map.fitBounds([[minLat, minLng], [maxLat, maxLng]], { padding: [50, 50] });
            }
        }
    }, [features, map]);

    return null;
};

// Invalidate map size when fullscreen changes
const MapSizeInvalidator = ({ isFullscreen }) => {
    const map = useMap();
    useEffect(() => {
        // Small delay to let the DOM resize complete before invalidating
        const timeout = setTimeout(() => {
            map.invalidateSize();
        }, 200);
        return () => clearTimeout(timeout);
    }, [isFullscreen, map]);
    return null;
};

// Pan map to GeoJSON boundary when no active cyclones
const FitToBoundary = ({ geoData }) => {
    const map = useMap();

    useEffect(() => {
        if (geoData && geoData.features && geoData.features.length > 0) {
            try {
                const geoJsonLayer = L.geoJSON(geoData);
                const bounds = geoJsonLayer.getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 9 });
                }
            } catch (e) {
                console.error("Error fitting to boundary:", e);
            }
        }
    }, [geoData, map]);

    return null;
};

const DisturbanceTrack = () => {
    const { hasPermission } = usePermissions();
    const [kmlData] = useState(null);
    const [styles] = useState([]);
    const [icons] = useState([]);
    const [legendImageUrls] = useState([]);
    const [municipalityData, setMunicipalityData] = useState(null);
    const [subMunicipalityData, setSubMunicipalityData] = useState(null);
    const [sucoData, setSucoData] = useState(null);
    const [impactedLocations] = useState([]);
    const [info, setInfo] = useState(false);
    const infoRef = useRef(null);
    const infoPanelRef = useCallback((node) => {
        infoRef.current = node;
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    useEffect(() => {
        if (!info) return;
        const handleClickOutside = (e) => {
            if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [info]);
    const [activeLayerType, setActiveLayerType] = useState('real');
    const [selectedCyclone, setSelectedCyclone] = useState('');
    const [showCycloneModal, setShowCycloneModal] = useState(false);
    const [selectedBasin, setSelectedBasin] = useState('');
    const [activeOptions, setActiveOptions] = useState(['gustForecast']);
    const [windProbLeadTimes, setWindProbLeadTimes] = useState([]);
    const [gfsRainfallLeadTimes, setGfsRainfallLeadTimes] = useState([]);
    const [showWindProb, setShowWindProb] = useState(false);
    const [showGfsRain, setShowGfsRain] = useState(false);
    const [activeCyclones, setActiveCyclones] = useState([]);
    const [loadingCyclones, setLoadingCyclones] = useState(false);
    const [fetchedKmlData, setFetchedKmlData] = useState(null);
    const [fetchedKmlStyles, setFetchedKmlStyles] = useState([]);
    const [fetchedLegendImageUrls, setFetchedLegendImageUrls] = useState([]);
    const [showAllPopups, setShowAllPopups] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [windSpeedLegendOpen, setWindSpeedLegendOpen] = useState(false);
    const [rainfallLegendOpen, setRainfallLegendOpen] = useState(false);
    const [showNoCycloneOverlay, setShowNoCycloneOverlay] = useState(true);
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [reportLoading, setReportLoading] = useState(false);
    const mapContainerRef = useRef(null);

    // Fullscreen toggle
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            mapContainerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    // Listen for fullscreen change to sync state
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const handleGenerateReportClick = () => {
        setShowLanguageModal(true);
    };

    const handleLanguageSelectAndGenerate = (selectedLanguage) => {
        setShowLanguageModal(false);
        captureAndGenerateReport(selectedLanguage);
    };

    // 1x1 transparent PNG, used when an image fails with an HTTP error.
    const BLANK_PNG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

    // dom-to-image-more fetches every <img> itself with XHR. When one can't be
    // fetched (cross-origin without CORS headers, blocked, offline) its
    // callback throws "readAsDataURL ... not of type Blob" and the capture
    // never finishes. So probe the images first and leave out the unfetchable
    // ones; the map still renders, just without those icons/tiles.
    const findUnfetchableImages = async (root) => {
        const sources = new Set();
        root.querySelectorAll('img').forEach((img) => {
            const src = img.currentSrc || img.src;
            if (src && !src.startsWith('data:')) sources.add(src);
        });
        const bad = new Set();
        await Promise.all([...sources].map(async (src) => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 8000);
            try {
                const res = await fetch(src, { mode: 'cors', cache: 'force-cache', signal: controller.signal });
                if (!res.ok) bad.add(src);
            } catch (e) {
                bad.add(src);
            } finally {
                clearTimeout(timer);
            }
        }));
        return bad;
    };

    const captureMapBlob = async (node) => {
        const options = {
            width: node.offsetWidth,
            height: node.offsetHeight,
            bgcolor: '#ffffff',
            style: { transform: 'none' },
            imagePlaceholder: BLANK_PNG,
        };
        // Never hang: if a capture stalls, give up on it and try the next fallback.
        const withTimeout = (promise, ms) => Promise.race([
            promise,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Map capture timed out')), ms)),
        ]);

        const bad = await findUnfetchableImages(node);
        const isImg = (n) => n.tagName === 'IMG' || n.tagName === 'IMAGE';
        const attempts = [
            // 1) everything except images that can't be fetched
            (n) => !(isImg(n) && bad.has(n.currentSrc || n.src || n.getAttribute?.('href'))),
            // 2) last resort: no <img> at all (boundaries / vector layers only)
            (n) => !isImg(n),
        ];
        let lastError = null;
        for (const filter of attempts) {
            try {
                return await withTimeout(domtoimage.toBlob(node, { ...options, filter }), 30000);
            } catch (e) {
                lastError = e;
                console.warn('Map capture attempt failed:', e);
            }
        }
        throw lastError || new Error('Map capture failed');
    };

    const captureAndGenerateReport = async (language) => {
        if (!mapContainerRef.current) {
            alert('PDF generation failed: map not ready. Please refresh and try again.');
            return;
        }
        setReportLoading(true);
        try {
            let mapBlob = null;
            try {
                mapBlob = await captureMapBlob(mapContainerRef.current);
            } catch (e) {
                console.warn('Map capture failed:', e);
            }
            await generateCycloneTrackingReport({
                language,
                cyclones: activeCyclones,
                selected: getSelectedCycloneRecord() || null,
                selectedBasin,
                activeLayers: activeOptions,
                mapBlob,
            });
        } catch (error) {
            console.error('Error generating cyclone report:', error);
            alert('Failed to generate report. Please try again.');
        } finally {
            setReportLoading(false);
        }
    };

    // Helper function to get unique basins from active cyclones
    const getUniqueBaains = (cyclones) => {
        const basins = [];
        const basinSet = new Set();
        cyclones.forEach((cyclone) => {
            if (!basinSet.has(cyclone.tsr_basin)) {
                basinSet.add(cyclone.tsr_basin);
                basins.push({ tsr_basin: cyclone.tsr_basin });
            }
        });
        return basins;
    };

    // Helper function to get cyclones for selected basin
    const getCyclonesForBasin = (basin) => {
        return activeCyclones.filter((cyclone) => cyclone.tsr_basin === basin);
    };

    // Helper function to get the selected cyclone record
    const getSelectedCycloneRecord = () => {
        if (!selectedCyclone) return null;
        const [cycloneName, agency] = selectedCyclone.split('-');
        return activeCyclones.find(
            (cyclone) => cyclone.name === cycloneName && cyclone.agency === agency
        );
    };

    // Helper function to get available products from selected cyclone
    const getAvailableProducts = () => {
        const record = getSelectedCycloneRecord();
        if (!record || !record.tsr_product) return [];
        
        const products = [];
        if (record.tsr_product.includes('GF')) products.push('gustForecast');
        if (record.tsr_product.includes('GH')) products.push('gustHistory');
        if (record.tsr_product.includes('WP')) products.push('windProb');
        if (record.tsr_product.includes('RA')) products.push('gfsRain');
        
        return products;
    };

    // Create animated cyclone icon
    // Create cyclone icon using GIF from public folder based on Storm Category
    const getCycloneIcon = (stormCategory) => {
        let iconFile = '/cyclone-green.gif'; // default

        if (stormCategory) {
            const cat = stormCategory.trim().toLowerCase();
            if (cat.includes('category 5')) {
                iconFile = '/cyclone-pink.gif';
            } else if (cat.includes('category 4')) {
                iconFile = '/cyclone-marker.gif';
            } else if (cat.includes('category 3')) {
                iconFile = '/cyclone-orange.gif';
            } else if (cat.includes('category 2')) {
                iconFile = '/cyclone-yellow2.gif';
            } else if (cat.includes('category 1')) {
                iconFile = '/cyclone-yellow.gif';
            } else if (cat.includes('tropical storm')) {
                iconFile = '/cyclone-green.gif';
            }
        }

        return L.icon({
            iconUrl: iconFile,
            iconSize: [40, 40],
            iconAnchor: [20, 20],
            popupAnchor: [0, -20]
        });
    };

    // Fetch active cyclones on component mount
    useEffect(() => {
        const loadActiveCyclones = async () => {
            setLoadingCyclones(true);
            try {
                const response = await fetchActiveCyclones();
                console.log('Raw response received from API:', response);
                
                // The API returns: { status: "success", data: [...] } where data is a flat array
                let processedCyclones = [];
                
                if (response && Array.isArray(response.data)) {
                    console.log('Processing data array:', response.data);
                    // Process each record directly from the flat array
                    response.data.forEach((record, index) => {
                        console.log('Processing record:', index, record);
                        processedCyclones.push({
                            name: record.name,
                            agency: record.agency || 'Unknown',
                            tsr_basin: record.tsr_basin,
                            tsr_product: record.tsr_product,
                            tsr_product_readable: convertProductCodes(record.tsr_product),
                            advisory_date: record.advisory_date,
                            stormid: record.stormid,
                            id: record.id,
                            updated_time: record.updated_time,
                            latest_update: record.latest_update,
                            latest_gfs_update: record.latest_gfs_update,
                            recordIndex: index // Track which record this is
                        });
                    });
                } else {
                    console.log('Response data structure not as expected:', response);
                }
                
                console.log('Processed cyclone array:', processedCyclones);
                console.log('Total cyclones processed:', processedCyclones.length);
                setActiveCyclones(processedCyclones);
                
                // Set default values from first active cyclone if available
                if (processedCyclones && processedCyclones.length > 0) {
                    console.log('Setting defaults from first cyclone:', processedCyclones[0]);
                    setSelectedBasin(processedCyclones[0].tsr_basin || '');
                    setSelectedCyclone(`${processedCyclones[0].name}-${processedCyclones[0].agency}` || '');
                } else {
                    console.log('No cyclone data available');
                }
            } catch (error) {
                console.error('Failed to load active cyclones:', error);
            } finally {
                setLoadingCyclones(false);
            }
        };
        loadActiveCyclones();
    }, []);

    useEffect(() => {
        const loadBoundaryData = async () => {
            try {
                const urls = [
                    "/DATA/OSMbased_adm1_boundary.json",
                    "/DATA/OSMbased_adm2_boundary.json",
                    "/DATA/OSMbased_adm3_boundary.json",
                ];
                const responses = await Promise.all(urls.map((url) => fetch(url)));
                const data = await Promise.all(responses.map((res) => res.json()));

                setMunicipalityData(data[0]);
                setSubMunicipalityData(data[1]);
                setSucoData(data[2]);
            } catch (error) {
                console.error("Error loading boundary data:", error);
            }
        };

        loadBoundaryData();
    }, []);

    // Auto-fetch and display data when clicking on "real" tab
    useEffect(() => {
        if (activeLayerType === 'real' && activeCyclones.length > 0 && !selectedCyclone) {
            // Get first basin and cyclone
            const firstBasin = activeCyclones[0]?.tsr_basin;
            const firstCyclone = activeCyclones[0];
            
            if (firstBasin && firstCyclone) {
                // Set basin and cyclone selections
                setSelectedBasin(firstBasin);
                setSelectedCyclone(`${firstCyclone.name}-${firstCyclone.agency}`);
                
                // Get first available product
                const selectedRecord = activeCyclones[0];
                if (selectedRecord && selectedRecord.tsr_product) {
                    const availableProducts = [];
                    if (selectedRecord.tsr_product.includes('GF')) availableProducts.push('gustForecast');
                    if (selectedRecord.tsr_product.includes('GH')) availableProducts.push('gustHistory');
                    if (selectedRecord.tsr_product.includes('WP')) availableProducts.push('windProb');
                    if (selectedRecord.tsr_product.includes('RA')) availableProducts.push('gfsRain');
                    
                    if (availableProducts.length > 0) {
                        setActiveOptions([availableProducts[0]]);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLayerType]);

    // Reset active options when cyclone selection changes on real-time tab
    useEffect(() => {
        if (activeLayerType === 'real' && selectedCyclone) {
            // Find the selected cyclone record
            const cycloneRecord = activeCyclones.find(c => `${c.name}-${c.agency}` === selectedCyclone);
            if (cycloneRecord && cycloneRecord.tsr_product) {
                const availableProducts = [];
                if (cycloneRecord.tsr_product.includes('GF')) availableProducts.push('gustForecast');
                if (cycloneRecord.tsr_product.includes('GH')) availableProducts.push('gustHistory');
                if (cycloneRecord.tsr_product.includes('WP')) availableProducts.push('windProb');
                if (cycloneRecord.tsr_product.includes('RA')) availableProducts.push('gfsRain');
                
                if (availableProducts.length > 0) {
                    // Only reset if the current active options are not available for this cyclone
                    const hasValidOption = activeOptions.some(opt => {
                        if (opt === 'gustForecast') return cycloneRecord.tsr_product.includes('GF');
                        if (opt === 'gustHistory') return cycloneRecord.tsr_product.includes('GH');
                        if (opt === 'windProb') return cycloneRecord.tsr_product.includes('WP');
                        if (opt === 'gfsRain') return cycloneRecord.tsr_product.includes('RA');
                        return false;
                    });
                    
                    if (!hasValidOption) {
                        setActiveOptions([availableProducts[0]]);
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCyclone, activeLayerType]);

    // Auto-fetch KML data when selections are made on "real" tab
    useEffect(() => {
        if (activeLayerType === 'real' && selectedCyclone && selectedBasin && activeOptions.length > 0) {
            const selectedRecord = getSelectedCycloneRecord();
            if (!selectedRecord) return;
            
            const productCodes = [];
            if (activeOptions.includes('gustForecast')) productCodes.push('GF');
            if (activeOptions.includes('gustHistory')) productCodes.push('GH');
            if (activeOptions.includes('windProb')) {
                // Add WP with lead times
                if (windProbLeadTimes.length > 0) {
                    windProbLeadTimes.forEach(leadTime => {
                        productCodes.push({ code: 'WP', leadTime });
                    });
                } else {
                    // Default to 0hrs if no lead time selected
                    productCodes.push({ code: 'WP', leadTime: '0hrs' });
                }
            }
            if (activeOptions.includes('gfsRain')) {
                // Add RA with lead times
                if (gfsRainfallLeadTimes.length > 0) {
                    gfsRainfallLeadTimes.forEach(leadTime => {
                        productCodes.push({ code: 'RA', leadTime });
                    });
                } else {
                    // Default to 12 if no lead time selected
                    productCodes.push({ code: 'RA', leadTime: '12' });
                }
            }
            
            if (productCodes.length === 0) return;
            
            const basePath = `https://gcf-app.rimes.int/tsr_current/2026/${selectedBasin}`;
            
            // Fetch all selected products and merge them
            const fetchPromises = productCodes.map(item => {
                const productCode = typeof item === 'string' ? item : item.code;
                const leadTime = typeof item === 'string' ? null : item.leadTime;
                
                let suffix = '';
                if (productCode === 'GF') suffix = 'gust';
                else if (productCode === 'GH') suffix = 'gustpast';
                else if (productCode === 'RA') {
                    const gfsDate = convertGfsDate(selectedRecord.latest_gfs_update);
                    const raMap = {
                        '12': `rain_12_${gfsDate}`,
                        '24': `rain_24_${gfsDate}`,
                        '36': `rain_36_${gfsDate}`,
                        '48': `rain_48_${gfsDate}`,
                        '72': `rain_72_${gfsDate}`,
                        '96': `rain_96_${gfsDate}`,
                        '120': `rain_120_${gfsDate}`
                    };
                    suffix = raMap[leadTime] || `12_${gfsDate}`;
                } else if (productCode === 'WP') {
                    const wpMap = {
                        '0hrs': '0_TSprob',
                        '12hrs': '1_TSprob',
                        '24hrs': '2_TSprob',
                        '36hrs': '3_TSprob'
                    };
                    suffix = wpMap[leadTime] || '0_TSprob';
                }
                
                let fileName = '';
                if (selectedRecord.agency === 'REU') {
                    fileName = `${selectedRecord.stormid}REU_${suffix}_${selectedRecord.advisory_date}.kml`;
                // } else if (selectedRecord.agency === 'BOM') {
                //     fileName = `${selectedRecord.stormid}_${suffix}_${selectedRecord.advisory_date}.kml`;
                } else {
                    fileName = `${selectedRecord.stormid}_${suffix}_${selectedRecord.advisory_date}.kml`;
                }
                
                const kmlUrl = `${basePath}/${selectedCyclone}/${productCode}/${fileName}`;
                console.log(`Fetching ${productCode}${leadTime ? ` (${leadTime})` : ''}: ${kmlUrl}`);
                
                return fetch(kmlUrl)
                    .then((response) => {
                        if (!response.ok) {
                            console.warn(`Failed to fetch ${productCode}${leadTime ? ` (${leadTime})` : ''} KML: ${response.status}`);
                            return null;
                        }
                        return response.text();
                    })
                    .catch((error) => {
                        console.warn(`Error fetching ${productCode}${leadTime ? ` (${leadTime})` : ''} KML:`, error.message);
                        return null;
                    });
            });
            
            // Wait for all fetches to complete
            Promise.all(fetchPromises).then((kmlTexts) => {
                const parser = new DOMParser();
                const allFeatures = [];
                const allStyles = [];
                const allLegends = [];
                
                // Process each KML file
                kmlTexts.forEach((kmlText, index) => {
                    if (!kmlText) return;
                    
                    try {
                        const kmlDoc = parser.parseFromString(kmlText, "text/xml");
                        const geojson = kml(kmlDoc);
                        
                        if (geojson && geojson.features && Array.isArray(geojson.features)) {
                            // Filter out features with null or missing geometry
                            const validFeatures = geojson.features.filter(f => f && f.geometry && f.geometry.type);
                            allFeatures.push(...validFeatures);
                        }

                        // Extract legend images from ScreenOverlay
                        const screenOverlays = kmlDoc.getElementsByTagName("ScreenOverlay");
                        for (let i = 0; i < screenOverlays.length; i++) {
                            const iconElement = screenOverlays[i].getElementsByTagName("Icon")[0];
                            if (iconElement) {
                                const hrefEl = iconElement.getElementsByTagName("href")[0];
                                const legendUrl = hrefEl ? hrefEl.textContent.trim() : iconElement.textContent.trim();
                                console.log('Legend URL extracted:', legendUrl);
                                if (legendUrl && !allLegends.includes(legendUrl)) {
                                    allLegends.push(legendUrl);
                                }
                            }
                        }
                        
                        // Extract styles
                        const extractedStyles = Array.from(
                            kmlDoc.getElementsByTagName("Style")
                        ).map((style) => {
                            const id = style.getAttribute("id");
                            const lineStyle = style.getElementsByTagName("LineStyle")[0];
                            const polyStyle = style.getElementsByTagName("PolyStyle")[0];
                            const iconStyle = style.getElementsByTagName("IconStyle")[0];
                            
                            let lineColor = "#000000";
                            let polyColor = "#ffffff";
                            let lineWidth = 2;
                            let iconUrl = null;
                            let iconScale = 1;
                            
                            if (lineStyle) {
                                const colorElement = lineStyle.getElementsByTagName("color")[0];
                                const widthElement = lineStyle.getElementsByTagName("width")[0];
                                if (colorElement) lineColor = convertKMLColor(colorElement.textContent);
                                if (widthElement) lineWidth = parseInt(widthElement.textContent);
                            }
                            
                            if (polyStyle) {
                                const colorElement = polyStyle.getElementsByTagName("color")[0];
                                if (colorElement) polyColor = convertKMLColor(colorElement.textContent);
                            }
                            
                            if (iconStyle) {
                                const iconElement = iconStyle.getElementsByTagName("Icon")[0];
                                const scaleElement = iconStyle.getElementsByTagName("scale")[0];
                                if (iconElement) {
                                    const hrefElement = iconElement.getElementsByTagName("href")[0];
                                    if (hrefElement) iconUrl = hrefElement.textContent;
                                }
                                if (scaleElement) iconScale = parseFloat(scaleElement.textContent);
                            }
                            
                            return {
                                id,
                                borderColor: lineColor,
                                fillColor: polyColor,
                                weight: lineWidth,
                                iconUrl,
                                iconScale
                            };
                        });
                        
                        allStyles.push(...extractedStyles);
                    } catch (error) {
                        console.error(`Error processing KML at index ${index}:`, error);
                    }
                });
                
                console.log('All KML files auto-fetched and merged successfully');
                
                // Log sample features with their styleUrl to debug matching
                const sampleFeatures = allFeatures.slice(0, 5);
                sampleFeatures.forEach((f, i) => {
                    console.log(`Sample feature ${i}:`, {
                        name: f.properties?.name,
                        type: f.geometry?.type,
                        styleUrl: f.properties?.styleUrl
                    });
                });
                
                // Log sample styles to debug matching
                const sampleStyles = allStyles.slice(0, 5);
                sampleStyles.forEach((s, i) => {
                    console.log(`Sample style ${i}:`, {
                        id: s.id,
                        borderColor: s.borderColor,
                        fillColor: s.fillColor,
                        weight: s.weight
                    });
                });
                
                // Set merged data (fix antimeridian crossing before rendering)
                const fixedFeatures = fixAntimeridianFeatures(allFeatures);
                setFetchedKmlData({
                    type: 'FeatureCollection',
                    features: fixedFeatures
                });
                setFetchedKmlStyles(allStyles);
                setFetchedLegendImageUrls(allLegends);
            }).catch((error) => {
                console.error('Error fetching KML files:', error.message);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLayerType, selectedCyclone, selectedBasin, activeOptions, windProbLeadTimes, gfsRainfallLeadTimes]);



    // Fix coordinates that cross the International Date Line (antimeridian at ±180°).
    // Handles three scenarios:
    // 1. Within-feature crossing: A single polygon/line has coords going from +179 to -175.
    //    Fixed by fixCoordSequence which makes them continuous (e.g., +179 → +181 → +185).
    // 2. Pre-split features: KML creator split a feature at ±180° into two halves.
    //    One half at [177,180], the other at [-180,-169]. Fixed by shifting the minority side.
    // 3. Mixed: Some features cross within themselves, others are pre-split.
    //    Fixed by applying fixCoordSequence first, then shifting based on the FIXED coordinates.
    const fixAntimeridianFeatures = (features) => {
        // Helper: fix a coordinate sequence where consecutive points jump across antimeridian.
        // After this, coordinates are continuous (no jumps > 180°).
        const fixCoordSequence = (coords) => {
            if (!coords || coords.length < 2) return coords;
            const fixed = [coords[0].slice()];
            for (let i = 1; i < coords.length; i++) {
                const prev = fixed[i - 1];
                const curr = coords[i].slice();
                let diff = curr[0] - prev[0];
                if (diff > 180) {
                    curr[0] -= 360;
                } else if (diff < -180) {
                    curr[0] += 360;
                }
                fixed.push(curr);
            }
            return fixed;
        };

        // Helper: shift all longitudes in a coordinate array by delta
        const shiftCoords = (coords, delta) => {
            return coords.map((c) => {
                const shifted = c.slice();
                shifted[0] += delta;
                return shifted;
            });
        };

        // Helper: get all longitudes from a geometry's coordinates
        const getLngsFromCoords = (geom) => {
            if (!geom) return [];
            if (geom.type === "Point") return [geom.coordinates[0]];
            if (geom.type === "LineString") return geom.coordinates.map(c => c[0]);
            if (geom.type === "Polygon") return geom.coordinates.flatMap(ring => ring.map(c => c[0]));
            if (geom.type === "MultiPolygon") return geom.coordinates.flatMap(poly => poly.flatMap(ring => ring.map(c => c[0])));
            return [];
        };

        // PHASE 1: Apply fixCoordSequence to all features first.
        // This makes within-feature antimeridian crossings continuous
        // (e.g., polygon going 179→-178 becomes 179→182).
        const phase1Features = features.map((feature) => {
            if (!feature || !feature.geometry) return feature;
            const geom = feature.geometry;
            if (geom.type === "LineString") {
                return { ...feature, geometry: { ...geom, coordinates: fixCoordSequence(geom.coordinates) } };
            } else if (geom.type === "Polygon") {
                return { ...feature, geometry: { ...geom, coordinates: geom.coordinates.map(ring => fixCoordSequence(ring)) } };
            } else if (geom.type === "MultiPolygon") {
                return { ...feature, geometry: { ...geom, coordinates: geom.coordinates.map(poly => poly.map(ring => fixCoordSequence(ring))) } };
            }
            return feature;
        });

        // PHASE 2: Check if the FIXED features still span both sides of ±180°.
        // After fixCoordSequence, within-feature crossings are resolved (coords may be >180 or <-180).
        // But pre-split features will still have one half on each side.
        let hasPositiveHigh = false;
        let hasNegativeHigh = false;

        phase1Features.forEach((feature) => {
            const lngs = getLngsFromCoords(feature?.geometry);
            lngs.forEach((lng) => {
                if (lng > 100) hasPositiveHigh = true;
                if (lng < -100) hasNegativeHigh = true;
            });
        });

        // If no antimeridian spanning after phase 1, we're done
        if (!hasPositiveHigh || !hasNegativeHigh) {
            return phase1Features;
        }

        // PHASE 3: Determine majority side from the FIXED coordinates
        let posCount = 0, negCount = 0;
        phase1Features.forEach((feature) => {
            const lngs = getLngsFromCoords(feature?.geometry);
            if (lngs.length === 0) return;
            const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;
            if (avgLng >= 0) posCount++;
            else negCount++;
        });

        const shiftNegToPos = posCount >= negCount;
        const delta = shiftNegToPos ? 360 : -360;

        console.log(`[Antimeridian Fix] Phase 2: posCount=${posCount}, negCount=${negCount}, shiftNegToPos=${shiftNegToPos}, total=${phase1Features.length}`);

        // PHASE 4: Shift features on the minority side to join the majority.
        // Use the FIXED coordinates' average to determine which side each feature is on.
        return phase1Features.map((feature) => {
            if (!feature || !feature.geometry) return feature;
            const geom = feature.geometry;
            const lngs = getLngsFromCoords(geom);
            if (lngs.length === 0) return feature;
            const avgLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

            // Only shift if the feature has high-magnitude longitudes (near antimeridian)
            // and is on the minority side
            const hasHighLng = lngs.some(lng => Math.abs(lng) > 90);
            const needsShift = hasHighLng && (shiftNegToPos ? (avgLng < 0) : (avgLng > 0));

            if (!needsShift) return feature;

            console.log(`[Antimeridian Fix] Shifting: type=${geom.type}, name="${feature.properties?.name || ''}", avgLng=${avgLng.toFixed(1)}, delta=${delta}`);

            if (geom.type === "Point") {
                const coord = geom.coordinates.slice();
                coord[0] += delta;
                return { ...feature, geometry: { ...geom, coordinates: coord } };
            } else if (geom.type === "LineString") {
                return { ...feature, geometry: { ...geom, coordinates: shiftCoords(geom.coordinates, delta) } };
            } else if (geom.type === "Polygon") {
                return { ...feature, geometry: { ...geom, coordinates: geom.coordinates.map(ring => shiftCoords(ring, delta)) } };
            } else if (geom.type === "MultiPolygon") {
                return { ...feature, geometry: { ...geom, coordinates: geom.coordinates.map(poly => poly.map(ring => shiftCoords(ring, delta))) } };
            }
            return feature;
        });
    };

    const convertKMLColor = (kmlColor) => {
        if (!kmlColor || kmlColor.length !== 8) return "#000000";
        const alpha = parseInt(kmlColor.slice(0, 2), 16) / 255;
        const blue = kmlColor.slice(2, 4);
        const green = kmlColor.slice(4, 6);
        const red = kmlColor.slice(6, 8);
        return `rgba(${parseInt(red, 16)}, ${parseInt(green, 16)}, ${parseInt(blue, 16)}, ${alpha.toFixed(2)})`;
    };

    // Define columns for MaterialReactTable
    const columns = useMemo(
        () => [
            {
                accessorKey: 'slNo',
                header: 'Sl No',
                enableColumnFilter: false, // You might not want to filter the serial number
                enableSorting: false, // You might not want to sort by serial number, it's just an index
                size: 50,
            },
            {
                accessorKey: 'municipality',
                header: 'Municipality',
                size: 150,
            },
            {
                accessorKey: 'postAdmin',
                header: 'Post Admin',
                size: 150,
            },
            {
                accessorKey: 'suco',
                header: 'Suco',
                size: 150,
            },
        ],
        [],
    );

    return (
        <>           
            {/* <div className="map-header-controls"> */}
                <div className="section-buttons-container" style={{ border: "0px" }}>
                    <button
                        className={`section-button ${activeLayerType === 'real' ? 'active' : ''}`}
                        onClick={() => setActiveLayerType('real')}
                    >
                        Real-Time Tracking
                    </button>
                    <button
                        className={`section-button ${activeLayerType === 'Radar' ? 'active' : ''}`}
                        onClick={() => setActiveLayerType('Radar')}
                    >
                        Radar
                    </button>
                    {/* Satellite Imagery now lives on the Dashboard's Forecast map tab — kept here, commented out, for reference.
                    <button
                        className={`section-button ${activeLayerType === 'SatelliteImagery' ? 'active' : ''}`}
                        onClick={() => setActiveLayerType('SatelliteImagery')}
                    >
                        Satellite Imagery
                    </button>
                    */}
                </div>
            {/* </div> */}

            {activeLayerType === 'real' && hasPermission('cyclone_disturbance_track_generate_report') && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <button
                        onClick={handleGenerateReportClick}
                        disabled={reportLoading || activeCyclones.length === 0}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            backgroundColor: reportLoading || activeCyclones.length === 0 ? '#90caf9' : '#0d6efd',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: reportLoading || activeCyclones.length === 0 ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 6px rgba(13, 110, 253, 0.3)',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {reportLoading ? (
                            <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                        ) : (
                            <DescriptionIcon sx={{ fontSize: 18 }} />
                        )}
                        Generate Report
                    </button>
                </Box>
            )}

            {/* {activeLayerType === 'SatelliteImagery' && <SatelliteImagery />} */}

            {activeLayerType !== 'SatelliteImagery' && (
            <div className="cyclone-map-container" ref={mapContainerRef} style={isFullscreen ? { height: '100vh', width: '100vw' } : {}}>
                {/* Fullscreen Toggle Button */}
                <button
                    onClick={toggleFullscreen}
                    style={{
                        position: 'absolute',
                        top: '120px',
                        left: '10px',
                        zIndex: 1000,
                        background: 'rgba(30, 41, 59, 0.45)',
                        backdropFilter: 'blur(10px) brightness(1.1)',
                        WebkitBackdropFilter: 'blur(10px) brightness(1.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        width: '34px',
                        height: '34px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff',
                        fontSize: '18px',
                        boxShadow: 'none',
                        transition: 'all 0.2s ease',
                    }}
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                    {isFullscreen ? '✕' : '⛶'}
                </button>
                {activeLayerType === 'real' && (
                    <>
                        {/* Arrow Button */}
                        <button
                            onClick={() => setShowCycloneModal(!showCycloneModal)}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                right: showCycloneModal ? '300px' : '20px',
                                transform: 'translateY(-50%)',
                                zIndex: 1001,
                                backgroundColor: '#1976d2',
                                border: 'none',
                                borderRadius: '6px',
                                width: '50px',
                                height: '50px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease',
                                color: 'white',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(25, 118, 210, 0.4)';
                                e.currentTarget.style.transform = 'translateY(-50%) scale(1.05)';
                                e.currentTarget.style.backgroundColor = '#1565c0';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.3)';
                                e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                                e.currentTarget.style.backgroundColor = '#1976d2';
                            }}
                        >
                            {showCycloneModal ? <FaChevronRight /> : <FaChevronLeft />}
                        </button>
                        {/* Sliding Modal */}
                        <div
                            style={{
                                position: 'absolute',
                                right: showCycloneModal ? '0' : '-300px',
                                top: 0,
                                height: '100%',
                                width: '300px',
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(12px) brightness(1.1)',
                                WebkitBackdropFilter: 'blur(12px) brightness(1.1)',
                                boxShadow: '-4px 0 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.3)',
                                zIndex: 1000,
                                transition: 'right 0.3s ease',
                                padding: '24px 20px',
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                                borderLeft: '1px solid rgba(255, 255, 255, 0.25)',
                            }}
                        >
                            {loadingCyclones && (
                                <Typography sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.9)', mb: 2, fontWeight: 500 }}>
                                    Loading cyclones...
                                </Typography>
                            )}
                            {!loadingCyclones && activeCyclones && activeCyclones.length === 0 && (
                                <Typography sx={{ fontSize: '14px', color: '#ff8a80', fontWeight: 'bold', mb: 2, textAlign: 'center', py: 3, backgroundColor: 'rgba(255, 82, 82, 0.15)', borderRadius: '6px', p: 2 }}>
                                    No cyclone active currently
                                </Typography>
                            )}
                            {activeCyclones && activeCyclones.length > 0 && (
                                <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', padding: '12px', borderRadius: '4px', marginBottom: '8px', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                                    <Typography sx={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>
                                        {activeCyclones.length} active cyclone(s) found
                                    </Typography>
                                </div>
                            )}
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '15px', color: 'white' }}>
                                Select Basin
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={selectedBasin}
                                    onChange={(e) => setSelectedBasin(e.target.value)}
                                    displayEmpty
                                    MenuProps={{
                                        container: mapContainerRef.current,
                                    }}
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        color: 'white',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        height: '42px',
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        transition: 'all 0.2s ease',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '& .MuiSelect-icon': {
                                            color: 'white',
                                        },
                                        '&:hover': {
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                            boxShadow: '0 2px 8px rgba(255, 255, 255, 0.1)',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                            boxShadow: '0 4px 12px rgba(255, 255, 255, 0.15)',
                                        },
                                    }}
                                >
                                    <MenuItem value="">Select a Basin</MenuItem>
                                    {activeCyclones && activeCyclones.length > 0 
                                        ? getUniqueBaains(activeCyclones).map((basin, index) => (
                                            <MenuItem key={index} value={basin.tsr_basin}>
                                                {basin.tsr_basin}
                                            </MenuItem>
                                        ))
                                        : (
                                            <>
                                                <MenuItem></MenuItem>
                                              
                                            </>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '15px', color: 'white' }}>
                                Select Cyclone
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={selectedCyclone}
                                    onChange={(e) => setSelectedCyclone(e.target.value)}
                                    displayEmpty
                                    MenuProps={{
                                        container: mapContainerRef.current,
                                    }}
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                        color: 'white',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        height: '42px',
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        transition: 'all 0.2s ease',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            border: 'none',
                                        },
                                        '& .MuiSelect-icon': {
                                            color: 'white',
                                        },
                                        '&:hover': {
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                            boxShadow: '0 2px 8px rgba(255, 255, 255, 0.1)',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: 'rgba(255, 255, 255, 0.5)',
                                            boxShadow: '0 4px 12px rgba(255, 255, 255, 0.15)',
                                        },
                                    }}
                                >
                                    <MenuItem value="">Select a Cyclone</MenuItem>
                                    {activeCyclones && activeCyclones.length > 0 && selectedBasin
                                        ? getCyclonesForBasin(selectedBasin).map((cyclone, index) => {
                                            const optionValue = `${cyclone.name}-${cyclone.agency}`;
                                            return (
                                                <MenuItem key={index} value={optionValue}>
                                                    {cyclone.name} ({cyclone.agency})
                                                </MenuItem>
                                            );
                                        })
                                        : !selectedBasin && (
                                            CYCLONE_FILES.map((cyclone) => (
                                                <MenuItem key={cyclone.id} value={cyclone.id}>
                                                    {cyclone.label}
                                                </MenuItem>
                                            ))
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1, mb: 1, fontSize: '15px', color: 'white' }}>
                                Active Display
                            </Typography>
                            {selectedCyclone ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {getAvailableProducts().includes('gustForecast') && (
                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                    size="small"
                                                    checked={activeOptions.includes('gustForecast')}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setActiveOptions([...activeOptions, 'gustForecast']);
                                                        } else {
                                                            setActiveOptions(activeOptions.filter(opt => opt !== 'gustForecast'));
                                                        }
                                                    }}
                                                    sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                />
                                            }
                                            label="Gust Forecast"
                                            sx={{ fontSize: '14px', color: 'white' }}
                                        />
                                    )}
                                    {getAvailableProducts().includes('gustHistory') && (
                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                    size="small"
                                                    checked={activeOptions.includes('gustHistory')}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setActiveOptions([...activeOptions, 'gustHistory']);
                                                        } else {
                                                            setActiveOptions(activeOptions.filter(opt => opt !== 'gustHistory'));
                                                        }
                                                    }}
                                                    sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                />
                                            }
                                            label="Gust History"
                                            sx={{ fontSize: '14px', color: 'white' }}
                                        />
                                    )}
                                    {getAvailableProducts().includes('windProb') && (
                                        <>
                                            <FormControlLabel 
                                                control={
                                                    <Checkbox 
                                                        size="small"
                                                        icon={<span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', width: 20, display: 'flex', justifyContent: 'center' }}>▶</span>}
                                                        checkedIcon={<span style={{ fontSize: '10px', color: 'white', width: 20, display: 'flex', justifyContent: 'center' }}>▼</span>}
                                                        checked={showWindProb}
                                                        onChange={() => setShowWindProb(!showWindProb)}
                                                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                                                    />
                                                }
                                                label="Wind Probabilities"
                                                sx={{ fontSize: '14px', color: 'white', cursor: 'pointer' }}
                                            />
                                            {showWindProb && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', ml: 3 }}>
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={windProbLeadTimes.includes('0hrs')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setWindProbLeadTimes([...windProbLeadTimes, '0hrs']);
                                                                        if (!activeOptions.includes('windProb')) setActiveOptions([...activeOptions, 'windProb']);
                                                                    } else {
                                                                        const updated = windProbLeadTimes.filter(t => t !== '0hrs');
                                                                        setWindProbLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'windProb'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="0 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={windProbLeadTimes.includes('12hrs')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setWindProbLeadTimes([...windProbLeadTimes, '12hrs']);
                                                                        if (!activeOptions.includes('windProb')) setActiveOptions([...activeOptions, 'windProb']);
                                                                    } else {
                                                                        const updated = windProbLeadTimes.filter(t => t !== '12hrs');
                                                                        setWindProbLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'windProb'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="12 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={windProbLeadTimes.includes('24hrs')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setWindProbLeadTimes([...windProbLeadTimes, '24hrs']);
                                                                        if (!activeOptions.includes('windProb')) setActiveOptions([...activeOptions, 'windProb']);
                                                                    } else {
                                                                        const updated = windProbLeadTimes.filter(t => t !== '24hrs');
                                                                        setWindProbLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'windProb'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="24 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={windProbLeadTimes.includes('36hrs')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setWindProbLeadTimes([...windProbLeadTimes, '36hrs']);
                                                                        if (!activeOptions.includes('windProb')) setActiveOptions([...activeOptions, 'windProb']);
                                                                    } else {
                                                                        const updated = windProbLeadTimes.filter(t => t !== '36hrs');
                                                                        setWindProbLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'windProb'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="36 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                </Box>
                                            )}
                                        </>
                                    )}
                                    {getAvailableProducts().includes('gfsRain') && (
                                        <>
                                            <FormControlLabel 
                                                control={
                                                    <Checkbox 
                                                        size="small"
                                                        icon={<span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', width: 20, display: 'flex', justifyContent: 'center' }}>▶</span>}
                                                        checkedIcon={<span style={{ fontSize: '10px', color: 'white', width: 20, display: 'flex', justifyContent: 'center' }}>▼</span>}
                                                        checked={showGfsRain}
                                                        onChange={() => setShowGfsRain(!showGfsRain)}
                                                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                                                    />
                                                }
                                                label="GFS Rainfall"
                                                sx={{ fontSize: '14px', color: 'white', cursor: 'pointer' }}
                                            />
                                            {showGfsRain && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', ml: 3 }}>
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={gfsRainfallLeadTimes.includes('12')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGfsRainfallLeadTimes([...gfsRainfallLeadTimes, '12']);
                                                                        if (!activeOptions.includes('gfsRain')) setActiveOptions([...activeOptions, 'gfsRain']);
                                                                    } else {
                                                                        const updated = gfsRainfallLeadTimes.filter(t => t !== '12');
                                                                        setGfsRainfallLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'gfsRain'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="12 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={gfsRainfallLeadTimes.includes('24')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGfsRainfallLeadTimes([...gfsRainfallLeadTimes, '24']);
                                                                        if (!activeOptions.includes('gfsRain')) setActiveOptions([...activeOptions, 'gfsRain']);
                                                                    } else {
                                                                        const updated = gfsRainfallLeadTimes.filter(t => t !== '24');
                                                                        setGfsRainfallLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'gfsRain'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="24 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={gfsRainfallLeadTimes.includes('36')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGfsRainfallLeadTimes([...gfsRainfallLeadTimes, '36']);
                                                                        if (!activeOptions.includes('gfsRain')) setActiveOptions([...activeOptions, 'gfsRain']);
                                                                    } else {
                                                                        const updated = gfsRainfallLeadTimes.filter(t => t !== '36');
                                                                        setGfsRainfallLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'gfsRain'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="36 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={gfsRainfallLeadTimes.includes('48')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGfsRainfallLeadTimes([...gfsRainfallLeadTimes, '48']);
                                                                        if (!activeOptions.includes('gfsRain')) setActiveOptions([...activeOptions, 'gfsRain']);
                                                                    } else {
                                                                        const updated = gfsRainfallLeadTimes.filter(t => t !== '48');
                                                                        setGfsRainfallLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'gfsRain'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="48 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={gfsRainfallLeadTimes.includes('72')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGfsRainfallLeadTimes([...gfsRainfallLeadTimes, '72']);
                                                                        if (!activeOptions.includes('gfsRain')) setActiveOptions([...activeOptions, 'gfsRain']);
                                                                    } else {
                                                                        const updated = gfsRainfallLeadTimes.filter(t => t !== '72');
                                                                        setGfsRainfallLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'gfsRain'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="72 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={gfsRainfallLeadTimes.includes('96')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGfsRainfallLeadTimes([...gfsRainfallLeadTimes, '96']);
                                                                        if (!activeOptions.includes('gfsRain')) setActiveOptions([...activeOptions, 'gfsRain']);
                                                                    } else {
                                                                        const updated = gfsRainfallLeadTimes.filter(t => t !== '96');
                                                                        setGfsRainfallLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'gfsRain'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="96 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                    <FormControlLabel 
                                                        control={
                                                            <Checkbox 
                                                                size="small"
                                                                checked={gfsRainfallLeadTimes.includes('120')}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setGfsRainfallLeadTimes([...gfsRainfallLeadTimes, '120']);
                                                                        if (!activeOptions.includes('gfsRain')) setActiveOptions([...activeOptions, 'gfsRain']);
                                                                    } else {
                                                                        const updated = gfsRainfallLeadTimes.filter(t => t !== '120');
                                                                        setGfsRainfallLeadTimes(updated);
                                                                        if (updated.length === 0) setActiveOptions(activeOptions.filter(opt => opt !== 'gfsRain'));
                                                                    }
                                                                }}
                                                                sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' } }}
                                                            />
                                                        }
                                                        label="120 hrs lead"
                                                        sx={{ fontSize: '13px', color: 'white' }}
                                                    />
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            ) : (
                                <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', padding: '12px', borderRadius: '4px', textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: '500' }}>
                                        Select a cyclone to view available products
                                    </Typography>
                                </div>
                            )}
                            {/* <Button 
                                variant="contained" 
                                color="primary" 
                                fullWidth 
                                sx={{ 
                                    mt: 3, 
                                    py: 1.5, 
                                    fontSize: '16px', 
                                    fontWeight: 'bold',
                                    backgroundColor: 'rgba(25, 118, 210, 0.7)',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.9)',
                                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                    },
                                    '&:active': {
                                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                                    }
                                }}
                                onClick={() => {
                                    const selectedRecord = getSelectedCycloneRecord();
                                    console.log('Submit clicked');
                                    console.log('Selected cyclone (name-agency):', selectedCyclone);
                                    console.log('=== FULL ROW DATA FROM ARRAY ===');
                                    console.table(selectedRecord);
                                    console.log('Full Record Object:', selectedRecord);
                                    console.log('====================================');
                                    console.log('Selected tsr_products:', getSelectedProductCodes());
                                    console.log('Wind Prob lead times:', windProbLeadTimes);
                                    console.log('GFS Rainfall lead times:', gfsRainfallLeadTimes);
                                    
                                    // Construct KML URLs for each selected product
                                    const productCodes = getSelectedProductCodes().split(',');
                                    const kmlUrls = [];
                                    
                                    productCodes.forEach(code => {
                                        const trimmedCode = code.trim();
                                        
                                        // For Wind Probabilities (WP), create URLs for each selected lead time
                                        if (trimmedCode === 'WP' && windProbLeadTimes.length > 0) {
                                            windProbLeadTimes.forEach(leadTime => {
                                                kmlUrls.push(getKmlUrl(trimmedCode, leadTime));
                                            });
                                        } else {
                                            // For other products, create URL without lead time
                                            kmlUrls.push(getKmlUrl(trimmedCode));
                                        }
                                    });
                                    
                                    console.log('=====================================');
                                    console.log('KML FILE URLs:');
                                    kmlUrls.forEach(url => console.log(url));
                                    console.log('=====================================');
                                    
                                    // Fetch the first KML file
                                    const kmlUrl = kmlUrls[0];
                                    fetch(kmlUrl)
                                        .then((response) => {
                                            if (!response.ok) {
                                                throw new Error(`Failed to fetch KML: ${response.status}`);
                                            }
                                            return response.text();
                                        })
                                        .then((kmlText) => {
                                            console.log('KML file fetched successfully');
                                            // Parse KML to GeoJSON
                                            const parser = new DOMParser();
                                            const kmlDoc = parser.parseFromString(kmlText, "text/xml");
                                            const geojson = kml(kmlDoc);
                                            
                                            // Extract styles from fetched KML
                                            const extractedFetchedStyles = Array.from(
                                                kmlDoc.getElementsByTagName("Style")
                                            ).map((style) => {
                                                const id = style.getAttribute("id");
                                                const lineStyle = style.getElementsByTagName("LineStyle")[0];
                                                const polyStyle = style.getElementsByTagName("PolyStyle")[0];
                                                const iconStyle = style.getElementsByTagName("IconStyle")[0];
                                                
                                                let lineColor = "#000000";
                                                let polyColor = "#ffffff";
                                                let lineWidth = 2;
                                                let iconUrl = null;
                                                let iconScale = 1;
                                                
                                                if (lineStyle) {
                                                    const colorElement = lineStyle.getElementsByTagName("color")[0];
                                                    const widthElement = lineStyle.getElementsByTagName("width")[0];
                                                    if (colorElement) lineColor = convertKMLColor(colorElement.textContent);
                                                    if (widthElement) lineWidth = parseInt(widthElement.textContent);
                                                }
                                                
                                                if (polyStyle) {
                                                    const colorElement = polyStyle.getElementsByTagName("color")[0];
                                                    if (colorElement) polyColor = convertKMLColor(colorElement.textContent);
                                                }
                                                
                                                if (iconStyle) {
                                                    const iconElement = iconStyle.getElementsByTagName("Icon")[0];
                                                    const scaleElement = iconStyle.getElementsByTagName("scale")[0];
                                                    if (iconElement) {
                                                        const hrefElement = iconElement.getElementsByTagName("href")[0];
                                                        if (hrefElement) iconUrl = hrefElement.textContent;
                                                    }
                                                    if (scaleElement) iconScale = parseFloat(scaleElement.textContent);
                                                }
                                                
                                                return {
                                                    id,
                                                    borderColor: lineColor,
                                                    fillColor: polyColor,
                                                    weight: lineWidth,
                                                    iconUrl,
                                                    iconScale
                                                };
                                            });
                                            
                                            console.log('KML converted to GeoJSON');
                                            console.log('GeoJSON:', geojson);
                                            console.log('Extracted Styles:', extractedFetchedStyles);
                                            setFetchedKmlData(geojson);
                                            setFetchedKmlStyles(extractedFetchedStyles);
                                        })
                                        .catch((error) => {
                                            console.error('Error fetching KML:', error.message);
                                        });
                                }}
                            >
                                Submit
                            </Button> */}
                        </div>
                    </>
                )}
                <MapContainer
                    key={activeLayerType}
                    center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
                    zoom={INITIAL_VIEW_STATE.zoom}
                    style={{ width: "100%", height: "100%" }}
                    worldCopyJump={true}
                >
                  <MapHomeControl />
                    <MapSizeInvalidator isFullscreen={isFullscreen} />
                    {/* Pan to country boundary when no active cyclones */}
                    {activeLayerType === 'real' && !loadingCyclones && activeCyclones && activeCyclones.length === 0 && municipalityData && (
                        <FitToBoundary geoData={municipalityData} />
                    )}
                    {/* Display Selected Basin and Cyclone at Top Left */}
                    {activeLayerType === 'real' && selectedBasin && selectedCyclone && (
                        <div
                            style={{
                                position: "absolute",
                                top: "20px",
                                left: "50px",
                                background: "rgba(30, 41, 59, 0.45)",
                                backdropFilter: "blur(10px) brightness(1.1)",
                                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                                padding: "20px 24px",
                                borderRadius: "16px",
                                zIndex: 999,
                                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.15)",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px",
                            }}
                        >
                            {/* Latest Update Row */}
                            <div>
                                <div style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "11px", fontWeight: "600", marginBottom: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                                    Latest Update
                                </div>
                                <div style={{ color: "#ffffff", fontSize: "14px", fontWeight: "700", letterSpacing: "0.5px" }}>
                                    {getSelectedCycloneRecord()?.latest_update || "N/A"}
                                </div>
                            </div>

                            {/* Divider */}
                            <div style={{ width: "100%", height: "1px", background: "rgba(255, 255, 255, 0.2)" }} />

                            {/* Main Content Row */}
                            <div style={{ display: "flex", gap: "32px", alignItems: "flex-start" }}>
                                {/* Basin Section */}
                                <div>
                                    <div style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "11px", fontWeight: "600", marginBottom: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                                        Basin
                                    </div>
                                    <div style={{ color: "#ffffff", fontSize: "16px", fontWeight: "700", letterSpacing: "0.5px" }}>
                                        {selectedBasin}
                                    </div>
                                </div>

                                {/* Divider */}
                                <div style={{ width: "1px", height: "80px", background: "rgba(255, 255, 255, 0.2)" }} />

                                {/* Cyclone and Agency Section */}
                                <div>
                                    <div style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "11px", fontWeight: "600", marginBottom: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                                        Cyclone
                                    </div>
                                    <div style={{ color: "#ffffff", fontSize: "16px", fontWeight: "700", letterSpacing: "0.5px", marginBottom: "12px" }}>
                                        {selectedCyclone}
                                    </div>
                                </div>

                                {/* Divider */}
                                <div style={{ width: "1px", height: "80px", background: "rgba(255, 255, 255, 0.2)" }} />

                                {/* Selected Products Section */}
                                <div>
                                    <div style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "11px", fontWeight: "600", marginBottom: "8px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                                        Active Display
                                    </div>
                                    <div style={{ color: "#ffffff", fontSize: "12px", fontWeight: "500", display: "flex", flexDirection: "column", gap: "4px" }}>
                                        {activeOptions.includes('gustForecast') && (
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "8px", height: "8px", background: "#fff", borderRadius: "50%" }} />
                                                Gust Forecast
                                            </div>
                                        )}
                                        {activeOptions.includes('gustHistory') && (
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "8px", height: "8px", background: "#fff", borderRadius: "50%" }} />
                                                Gust History
                                            </div>
                                        )}
                                        {activeOptions.includes('windProb') && (
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "8px", height: "8px", background: "#fff", borderRadius: "50%" }} />
                                                Wind Probabilities
                                            </div>
                                        )}
                                        {activeOptions.includes('gfsRain') && (
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <span style={{ width: "8px", height: "8px", background: "#fff", borderRadius: "50%" }} />
                                                GFS Rainfall
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Display Latest Update at Top Right */}

                    {info && (
                        <div
                            ref={infoPanelRef}
                            style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
                        >
                            Active cyclone track information will be displayed here. If no active track is available, this page will indicate that there are currently no active cyclone warnings.
                        </div>
                    )}
                    <InfoButton onClick={() => setInfo(!info)} />

                    {/* Toggle button to show/hide all popups */}
                    {activeLayerType === 'real' && fetchedKmlData && (
                        <div
                            style={{
                                position: "absolute",
                                bottom: "30px",
                                left: "12px",
                                zIndex: 1000,
                            }}
                        >
                            <button
                                onClick={() => setShowAllPopups(prev => !prev)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "8px 14px",
                                    background: "rgba(30, 41, 59, 0.45)",
                                    backdropFilter: "blur(10px) brightness(1.1)",
                                    WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                                    color: "#ffffff",
                                    border: "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: "10px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.15)",
                                    transition: "all 0.2s ease",
                                    letterSpacing: "0.3px",
                                }}
                                title={showAllPopups ? "Hide all labels" : "Show all labels"}
                            >
                                {showAllPopups ? <CloseIcon style={{ fontSize: 16 }} /> : <CheckIcon style={{ fontSize: 16 }} />}
                                {showAllPopups ? "Hide All Labels" : "Show All Labels"}
                            </button>
                        </div>
                    )}

                    {activeLayerType !== 'real' && kmlData && (
                        <MapViewAdjuster features={kmlData.features} />
                    )}

                    <LayersControl position="topright">
                        <LayersControl.BaseLayer name="Satellite" checked>
                            <TileLayer
                                url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                                attribution='Map data © Tropical Storm Risk (TSR)'
                            />
                        </LayersControl.BaseLayer>

                        {municipalityData && (
                            <LayersControl.Overlay name="Municipality" >
                                <GeoJSON
                                    data={municipalityData}
                                    style={{
                                        color: "#333333",
                                        weight: 1,
                                        opacity: 1,
                                        // fillColor: "#87CEFA",
                                        fillOpacity: 0,
                                    }}
                                />
                            </LayersControl.Overlay>
                        )}

                        {subMunicipalityData && (
                            <LayersControl.Overlay name="Sub-Municipality">
                                <GeoJSON
                                    data={subMunicipalityData}
                                    style={{
                                        color: "#333333",
                                        weight: 1,
                                        opacity: 1,
                                        // fillColor: "#ADD8E6",
                                        fillOpacity: 0,
                                    }}
                                />
                            </LayersControl.Overlay>
                        )}

                        {sucoData && (
                            <LayersControl.Overlay name="Suco" checked>
                                <GeoJSON
                                    data={sucoData}
                                    style={{
                                        color: "#333333",
                                        weight: 0.5,
                                        opacity: 1,
                                        // fillColor: "#B0E0E6",
                                        fillOpacity: 0,
                                    }}
                                />
                            </LayersControl.Overlay>
                        )}
                    </LayersControl>
                    {/* <div className="source-note">
                                Source: Tropical Storm Risk (TSR)
                            </div> */}

                    {/* Render KML data for non-real modes (Forecast/Satellite/Radar) */}
                    {activeLayerType !== 'real' && kmlData && kmlData.features &&
                        kmlData.features.map((feature, idx) => {
                            const currentStyles = styles;
                            const currentIcons = icons;
                            const isTestMode = false;
                            
                            if (feature.geometry.type === "Polygon") {
                                const styleUrl = feature.properties.styleUrl || '';
                                // Remove # prefix from styleUrl for matching
                                const styleId = styleUrl.replace('#', '');
                                
                                const style = currentStyles.find((s) => s.id === styleId) || {
                                    borderColor: "#000000",
                                    fillColor: "#ffffff",
                                };

                                // Determine risk level for enhanced styling
                                let riskClass = '';
                                const colorLower = style.fillColor.toLowerCase();
                                if (colorLower.includes('e6') || colorLower.includes('ff0000') || colorLower.includes('230, 45')) {
                                    riskClass = 'high-risk-zone';
                                } else if (colorLower.includes('f3') || colorLower.includes('ff8800')) {
                                    riskClass = 'medium-risk-zone';
                                } else if (colorLower.includes('f3') || colorLower.includes('ffff00')) {
                                    riskClass = 'low-risk-zone';
                                }

                                // Use Polygon component instead of GeoJSON to preserve
                                // antimeridian-fixed coordinates (GeoJSON normalizes back to [-180,180])
                                const positions = feature.geometry.coordinates.map((ring) =>
                                    ring.map((coord) => [coord[1], coord[0]])
                                );

                                return (
                                    <Polygon
                                        key={idx}
                                        positions={positions}
                                        className={riskClass}
                                        pathOptions={{
                                            color: style.borderColor,
                                            weight: isTestMode ? 3 : 2,
                                            opacity: isTestMode ? 0.9 : 0.8,
                                            fillColor: style.fillColor,
                                            fillOpacity: isTestMode ? 0.5 : 0.4,
                                        }}
                                        eventHandlers={{
                                            click: (e) => {
                                                e.target
                                                    .bindPopup(`${feature.properties.name}`)
                                                    .openPopup();
                                            },
                                        }}
                                    />
                                );
                            } else if (feature.geometry.type === "Point") {
                                const iconStyle = currentIcons.find((icon) =>
                                    feature.properties.styleUrl?.includes(icon.id)
                                );

                                // Skip rendering marker if icon is not available
                                if (!iconStyle || !iconStyle.iconUrl) {
                                    return null;
                                }

                                const icon = L.icon({
                                    iconUrl: iconStyle.iconUrl,
                                    iconSize: [32, 32],
                                });

                                return (
                                    <Marker
                                        key={idx}
                                        position={[
                                            feature.geometry.coordinates[1],
                                            feature.geometry.coordinates[0],
                                        ]}
                                        icon={icon}
                                    >
                                        <Popup>
                                            <div>
                                                <h3 style={{ fontWeight: "bold", textAlign: "center" }}>
                                                    {feature.properties["name"] || "N/A" || feature.properties["id"]}
                                                </h3>
                                                <table
                                                    style={{
                                                        borderCollapse: "collapse",
                                                        width: "100%",
                                                        fontSize: "5px",
                                                    }}
                                                >
                                                    <tbody>
                                                        {[
                                                            ["Date", feature.properties["Date"]],
                                                            [
                                                                "Maximum 10-min Sustained Wind",
                                                                feature.properties[
                                                                "Maximum 10-min Sustained Wind"
                                                                ],
                                                            ],
                                                            ["Storm Category", feature.properties["Storm Category"]],
                                                            ["Storm Position", feature.properties["Storm Position"]],
                                                        ].map(([label, value]) =>
                                                            value ? (
                                                                <tr key={label}>
                                                                    <td
                                                                        style={{
                                                                            border: "1px solid black",
                                                                            padding: "5px",
                                                                            fontWeight: "bold",
                                                                            width: "35%",
                                                                        }}
                                                                    >
                                                                        {label}
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            border: "1px solid black",
                                                                            padding: "5px",
                                                                        }}
                                                                    >
                                                                        {value}
                                                                    </td>
                                                                </tr>
                                                            ) : null
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            } else if (feature.geometry.type === "LineString") {
                                const styleUrl = feature.properties.styleUrl || '';
                                // Remove # prefix from styleUrl for matching
                                const styleId = styleUrl.replace('#', '');
                                
                                const style = currentStyles.find((s) => s.id === styleId) || {
                                    borderColor: "#FF0000",
                                };

                                return (
                                    <Polyline
                                        key={idx}
                                        positions={feature.geometry.coordinates.map((coord) => [
                                            coord[1],
                                            coord[0],
                                        ])}
                                        pathOptions={{
                                            color: style.borderColor,
                                            weight: isTestMode ? 4 : 3,
                                            opacity: isTestMode ? 0.9 : 0.8,
                                            dashArray: isTestMode ? '10, 5' : undefined,
                                            lineCap: 'round',
                                            lineJoin: 'round',
                                        }}
                                    >
                                        <Popup>{feature.properties.name}</Popup>
                                    </Polyline>
                                );
                            }
                            return null;
                        })}

                    {/* Render fetched cyclone KML file */}
                    {activeLayerType === 'real' && fetchedKmlData && fetchedKmlData.features && (
                        <>
                            <MapViewAdjuster features={fetchedKmlData.features.filter(f => f && f.geometry)} />
                            {fetchedKmlData.features.filter(f => f && f.geometry && f.geometry.type).map((feature, idx) => {
                                if (!feature || !feature.geometry || !feature.geometry.type) return null;
                                if (feature.geometry.type === "Point") {
                                    return (
                                        <Marker
                                            key={`fetched-marker-${idx}`}
                                            position={[
                                                feature.geometry.coordinates[1],
                                                feature.geometry.coordinates[0],
                                            ]}
                                            icon={getCycloneIcon(feature.properties["Storm Category"])}
                                        >
                                            <Popup>
                                                <div style={{ maxWidth: "300px",  borderRadius: "4px", overflow: "hidden" }}>
                                                    <h4 style={{ marginBottom: "10px", fontWeight: "bold", fontSize: '16px', backgroundColor: '#007bff', color: 'white', padding: '8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px', margin: '0' }}>
                                                        <FaCircleInfo size={18} style={{ flexShrink: 0 }} />
                                                        {feature.properties.name || "Point Information"}
                                                    </h4>
                                                    <table style={{ width: "100%", borderCollapse: "collapse", background: "#e3f2fd", fontSize: "12px", borderRadius: "4px", overflow: "hidden" }}>
                                                        <tbody>
                                                            {[
                                                                { label: "Maximum 1-min Sustained Wind", key: "Maximum 1-min Sustained Wind" },
                                                                { label: "Maximum 10-min Sustained Wind", key: "Maximum 10-min Sustained Wind" },
                                                                { label: "Date", key: "Date" },
                                                                { label: "Storm Category", key: "Storm Category" },
                                                                { label: "Storm Position", key: "Storm Position" }
                                                            ].map(({ label, key }) => (
                                                                feature.properties[key] ? (
                                                                    <tr key={key}>
                                                                        <td style={{ border: "1px solid #ddd", padding: "5px", fontWeight: "bold", width: "40%", color: "#1565c0" }}>
                                                                            {label}
                                                                        </td>
                                                                        <td style={{ border: "1px solid #ddd", padding: "5px" }}>
                                                                            {String(feature.properties[key])}
                                                                        </td>
                                                                    </tr>
                                                                ) : null
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </Popup>
                                            {showAllPopups && (
                                                <Tooltip
                                                    permanent
                                                    direction="top"
                                                    offset={[0, -20]}
                                                    className="cyclone-permanent-tooltip"
                                                >
                                                    <div style={{ fontSize: "11px", lineHeight: "1.4", minWidth: "140px" }}>
                                                        <div style={{ fontWeight: "700", color: "#1565c0", marginBottom: "3px", borderBottom: "1px solid #e0e0e0", paddingBottom: "3px" }}>
                                                            {feature.properties.name || "N/A"}
                                                        </div>
                                                        {feature.properties["Storm Category"] && (
                                                            <div><strong>Cat:</strong> {feature.properties["Storm Category"]}</div>
                                                        )}
                                                        {feature.properties["Date"] && (
                                                            <div><strong>Date:</strong> {feature.properties["Date"]}</div>
                                                        )}
                                                        {feature.properties["Maximum 10-min Sustained Wind"] && (
                                                            <div><strong>Wind:</strong> {feature.properties["Maximum 10-min Sustained Wind"]}</div>
                                                        )}
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </Marker>
                                    );
                                } else if (feature.geometry.type === "Polygon") {
                                    const styleUrl = feature.properties.styleUrl || '';
                                    // Remove # prefix from styleUrl for matching
                                    const styleId = styleUrl.replace('#', '');
                                    
                                    const style = fetchedKmlStyles.find((s) => s.id === styleId) || {
                                        borderColor: "#000000",
                                        fillColor: "#ffffff",
                                        weight: 2
                                    };
                                    
                                    // Use Polygon component instead of GeoJSON to preserve
                                    // antimeridian-fixed coordinates (GeoJSON normalizes back to [-180,180])
                                    const positions = feature.geometry.coordinates.map((ring) =>
                                        ring.map((coord) => [coord[1], coord[0]])
                                    );
                                    
                                    return (
                                        <Polygon
                                            key={`fetched-poly-${idx}`}
                                            positions={positions}
                                            pathOptions={{
                                                color: style.borderColor,
                                                weight: style.weight || 2,
                                                opacity: 0.8,
                                                fillColor: style.fillColor,
                                                fillOpacity: 0.5,
                                            }}
                                            eventHandlers={{
                                                click: (e) => {
                                                    const popupContent = `
                                        <div style="padding: 10px; font-family: Arial, sans-serif;">
                                            <p style="margin: 0 0 8px 0; color: #333;">
                                              ${feature.properties.name || 'Feature Information'}
                                            </p>
                                        </div>
                                    `;
                                    e.target.bindPopup(popupContent).openPopup();
                                }
                            }}
                                        />
                                    );
                                } else if (feature.geometry.type === "LineString") {
                                    const styleUrl = feature.properties.styleUrl || '';
                                    // Remove # prefix from styleUrl for matching
                                    const styleId = styleUrl.replace('#', '');
                                    
                                    const style = fetchedKmlStyles.find((s) => s.id === styleId) || {
                                        borderColor: "#FF0000",
                                        weight: 2
                                    };
                                    
                                    if (idx < 5) {
                                        console.log(`LineString match - styleUrl: "${styleUrl}", styleId: "${styleId}", found: ${!!fetchedKmlStyles.find((s) => s.id === styleId)}`);
                                    }
                                    
                                    return (
                                        <Polyline
                                            key={`fetched-line-${idx}`}
                                            positions={feature.geometry.coordinates.map((coord) => [
                                                coord[1],
                                                coord[0],
                                            ])}
                                            pathOptions={{
                                                color: style.borderColor,
                                                weight: style.weight || 2,
                                                opacity: 0.8,
                                                lineCap: 'round',
                                                lineJoin: 'round',
                                            }}
                                        >
                                            <Popup>{feature.properties.name}</Popup>
                                        </Polyline>
                                    );
                                }
                                return null;
                            })}
                        </>
                    )}
                </MapContainer>

                {/* No Active Cyclone Overlay on Map */}
                {activeLayerType === 'real' && !loadingCyclones && activeCyclones && activeCyclones.length === 0 && showNoCycloneOverlay && (
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 1000,
                            background: "rgba(30, 41, 59, 0.45)",
                            backdropFilter: "blur(12px) brightness(1.1)",
                            WebkitBackdropFilter: "blur(12px) brightness(1.1)",
                            padding: "24px 36px",
                            borderRadius: "16px",
                            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.15)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            textAlign: "center",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                        }}
                    >
                        <button
                            onClick={() => setShowNoCycloneOverlay(false)}
                            style={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                background: "rgba(255, 255, 255, 0.15)",
                                border: "1px solid rgba(255, 255, 255, 0.3)",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                color: "rgba(255, 255, 255, 0.8)",
                                fontSize: "14px",
                                lineHeight: 1,
                                padding: 0,
                                transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                                e.currentTarget.style.color = "white";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
                                e.currentTarget.style.color = "rgba(255, 255, 255, 0.8)";
                            }}
                            title="Close"
                        >
                            ✕
                        </button>
                        <div style={{ color: "#ffffff", fontSize: "16px", fontWeight: "700", letterSpacing: "0.3px" }}>
                            No Cyclone Active Currently
                        </div>
                        <div style={{ color: "rgba(255, 255, 255, 0.85)", fontSize: "12px", marginTop: "6px", fontWeight: "500" }}>
                            There are no active cyclone warnings at this time
                        </div>
                    </div>
                )}

                {/* Storm Category Legend */}
                {activeLayerType === 'real' && fetchedKmlData && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: "20px",
                            left: "12px",
                            background: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(10px) brightness(1.1)",
                            WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                            padding: "12px 16px",
                            borderRadius: "12px",
                            zIndex: 1000,
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                            border: "1px solid rgba(0, 0, 0, 0.08)",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            minWidth: "140px",
                        }}
                    >
                        <div style={{ color: "#333333", fontSize: "11px", fontWeight: "700", marginBottom: "10px", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                            Storm Category
                        </div>
                        {[
                            { label: "Tropical Storm", color: "#4fff00" },
                            { label: "Category 1", color: "#fdff00" },
                            { label: "Category 2", color: "#fac800" },
                            { label: "Category 3", color: "#f66604" },
                            { label: "Category 4", color: "#f40305" },
                            { label: "Category 5", color: "#cc02cc" },
                        ].map(({ label, color }) => (
                            <div
                                key={label}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "6px",
                                }}
                            >
                                <span
                                    style={{
                                        width: "14px",
                                        height: "14px",
                                        backgroundColor: color,
                                        borderRadius: "50%",
                                        border: "1.5px solid rgba(255, 255, 255, 0.6)",
                                        flexShrink: 0,
                                        boxShadow: `0 0 6px ${color}80`,
                                    }}
                                />
                                <span style={{ color: "#333333", fontSize: "11px", fontWeight: "500" }}>
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Wind Speed Scale Legend */}
                {activeLayerType === 'real' && fetchedKmlData && (activeOptions.includes('gustForecast') || activeOptions.includes('gustHistory')) && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: "20px",
                            left: "12px",
                            background: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(10px) brightness(1.1)",
                            WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                            padding: "12px 16px",
                            borderRadius: "12px",
                            zIndex: 1000,
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                            border: "1px solid rgba(0, 0, 0, 0.08)",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            minWidth: "160px",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                        }}
                    >
                        <div
                            onClick={() => setWindSpeedLegendOpen(!windSpeedLegendOpen)}
                            style={{ color: "#333333", fontSize: "11px", fontWeight: "700", letterSpacing: "0.5px", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", userSelect: "none" }}
                        >
                            Wind Speed (m/h)
                            <span style={{ fontSize: "10px", transition: "transform 0.3s ease", transform: windSpeedLegendOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                        </div>
                        {windSpeedLegendOpen && (
                            <div style={{ marginTop: "10px" }}>
                                {[
                                    { label: "0 – 40", color: "#99ffff" },
                                    { label: "40 – 60", color: "#55fffe" },
                                    { label: "50 – 60", color: "#53ffcc" },
                                    { label: "60 – 70", color: "#4ffe64" },
                                    { label: "70 – 80", color: "#4eff00" },
                                    { label: "80 – 90", color: "#66ff00" },
                                    { label: "90 – 100", color: "#ccfe00" },
                                    { label: "100 – 110", color: "#feff00" },
                                    { label: "110 – 120", color: "#facc03" },
                                    { label: "120 – 130", color: "#f89a04" },
                                    { label: "130 – 140", color: "#f66603" },
                                    { label: "140 – 150", color: "#f40202" },
                                    { label: "150 – 160", color: "#cc01ca" },
                                    { label: "160 – 170", color: "#9a00ff" },
                                    { label: "170 – 180", color: "#cc02cc" },
                                    { label: "180 – 190", color: "#650199" },
                                    { label: "≥ 190", color: "#650164" },
                                ].map(({ label, color }) => (
                                    <div
                                        key={label}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "6px",
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: "28px",
                                                height: "6px",
                                                backgroundColor: color,
                                                borderRadius: "3px",
                                                border: "1px solid rgba(255, 255, 255, 0.4)",
                                                flexShrink: 0,
                                                boxShadow: `0 0 6px ${color}80`,
                                            }}
                                        />
                                        <span style={{ color: "#333333", fontSize: "11px", fontWeight: "500" }}>
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Rainfall Legend */}
                {activeLayerType === 'real' && fetchedKmlData && activeOptions.includes('gfsRain') && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: "20px",
                            left: "12px",
                            background: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(10px) brightness(1.1)",
                            WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                            padding: "12px 16px",
                            borderRadius: "12px",
                            zIndex: 1000,
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                            border: "1px solid rgba(0, 0, 0, 0.08)",
                            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                            minWidth: "150px",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                        }}
                    >
                        <div
                            onClick={() => setRainfallLegendOpen(!rainfallLegendOpen)}
                            style={{ color: "#333333", fontSize: "11px", fontWeight: "700", letterSpacing: "0.5px", textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", userSelect: "none" }}
                        >
                            Rainfall (mm)
                            <span style={{ fontSize: "10px", transition: "transform 0.3s ease", transform: rainfallLegendOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                        </div>
                        {rainfallLegendOpen && (
                            <div style={{ marginTop: "10px" }}>
                                {[
                                    { label: "20 – 40", color: "#99ffff" },
                                    { label: "40 – 60", color: "#55fffe" },
                                    { label: "60 – 80", color: "#53ffcc" },
                                    { label: "80 – 100", color: "#4ffe64" },
                                    { label: "100 – 120", color: "#4eff00" },
                                    { label: "120 – 140", color: "#66ff00" },
                                    { label: "140 – 160", color: "#ccfe00" },
                                    { label: "160 – 180", color: "#feff00" },
                                    { label: "180 – 200", color: "#facc03" },
                                    { label: "200 – 220", color: "#f89a04" },
                                    { label: "220 – 240", color: "#f66603" },
                                    { label: "240 – 260", color: "#f44d03" },
                                    { label: "260 – 280", color: "#f40202" },
                                    { label: "280 – 300", color: "#cc01ca" },
                                    { label: "300 – 320", color: "#9a00ff" },
                                    { label: "320 – 340", color: "#cc02cc" },
                                    { label: "340 – 360", color: "#650199" },
                                    { label: "360 – 380", color: "#650164" },
                                    { label: "380 – 400", color: "#4b014b" },
                                    { label: "≥ 400", color: "#330033" },
                                ].map(({ label, color }) => (
                                    <div
                                        key={label}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            marginBottom: "6px",
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: "28px",
                                                height: "6px",
                                                backgroundColor: color,
                                                borderRadius: "3px",
                                                border: "1px solid rgba(255, 255, 255, 0.4)",
                                                flexShrink: 0,
                                                boxShadow: `0 0 6px ${color}80`,
                                            }}
                                        />
                                        <span style={{ color: "#333333", fontSize: "11px", fontWeight: "500" }}>
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {((activeLayerType === 'real' ? fetchedLegendImageUrls : legendImageUrls).length > 0 && (activeLayerType === 'real' ? fetchedLegendImageUrls : legendImageUrls)[0]) && (
                    <div className="map-legends intensity-legend">
                        <img src={(activeLayerType === 'real' ? fetchedLegendImageUrls : legendImageUrls)[0]} alt="Intensity Legend" />
                    </div>
                )}
                {((activeLayerType === 'real' ? fetchedLegendImageUrls : legendImageUrls).length > 1 && (activeLayerType === 'real' ? fetchedLegendImageUrls : legendImageUrls)[1]) && (
                    <div className="map-legends normal-legend">
                        <img src={(activeLayerType === 'real' ? fetchedLegendImageUrls : legendImageUrls)[1]} alt="Normal Legend" />
                    </div>
                )}

            </div>
            )}
            {impactedLocations.length > 0 && activeLayerType !== 'real' && (
                    <Box sx={{ p: 2, borderTop: '1px solid #ddd', mt: 2 }}>
                        <MaterialReactTable
                            columns={columns}
                            data={impactedLocations}
                            enableColumnFilters
                            enableColumnActions
                            enableSorting
                            enablePagination
                            enableDensityToggle={true}
                            enableFullScreenToggle={false}
                            initialState={{
                                columnVisibility: { slNo: true }, // Ensure Sl No is visible by default
                                density: 'compact',
                            }}
                            renderTopToolbarCustomActions={({ table }) => (
                                <Box sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ mr: 'auto' }}>
                                        Impacted Areas
                                    </Typography>
                                </Box>
                            )}
                            muiTableContainerProps={{ sx: { maxHeight: '400px' } }}
                            muiTableHeadCellProps={{
                                sx: {
                                    fontWeight: 'bold',
                                    backgroundColor: '#e0e0e0',
                                },
                            }}
                        />
                    </Box>
                )}

            <Modal
                show={showLanguageModal}
                onHide={() => setShowLanguageModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Body style={{ padding: 0, border: 'none', background: 'transparent' }}>
                    <div
                        style={{
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            padding: '32px 36px',
                            textAlign: 'center',
                        }}
                    >
                        <div style={{
                            width: '52px',
                            height: '52px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #e8f0fe, #d4e4fd)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                            fontSize: '22px',
                        }}>
                            🌐
                        </div>

                        <h3 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: 700, color: '#1a1a2e' }}>
                            Select Report Language
                        </h3>
                        <p style={{ margin: '0 0 24px', fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
                            Choose the language for the generated advisory PDF report.
                        </p>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <div
                                onClick={() => handleLanguageSelectAndGenerate('en')}
                                style={{
                                    flex: 1,
                                    maxWidth: '160px',
                                    padding: '18px 12px',
                                    borderRadius: '12px',
                                    border: '1.5px solid #e2e8f0',
                                    backgroundColor: '#fafbfc',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#0d6efd';
                                    e.currentTarget.style.backgroundColor = '#f0f6ff';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                    e.currentTarget.style.backgroundColor = '#fafbfc';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <span style={{ fontSize: '28px' }}>🇬🇧</span>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>English</span>
                            </div>

                            <div
                                onClick={() => handleLanguageSelectAndGenerate('tetum')}
                                style={{
                                    flex: 1,
                                    maxWidth: '160px',
                                    padding: '18px 12px',
                                    borderRadius: '12px',
                                    border: '1.5px solid #e2e8f0',
                                    backgroundColor: '#fafbfc',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = '#28a745';
                                    e.currentTarget.style.backgroundColor = '#f0faf3';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                    e.currentTarget.style.backgroundColor = '#fafbfc';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <span style={{ fontSize: '28px' }}>🇹🇱</span>
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>Tetum</span>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowLanguageModal(false)}
                            style={{
                                marginTop: '20px',
                                padding: '8px 24px',
                                backgroundColor: 'transparent',
                                color: '#6b7280',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                                e.currentTarget.style.borderColor = '#d1d5db';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.borderColor = '#e2e8f0';
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default DisturbanceTrack;